import styled from 'styled-components';
import * as Yup from 'yup';
import type { FormikProps } from 'formik';
import type { RequiredStringSchema } from 'yup/lib/string';
import type { OptionalObjectSchema } from 'yup/lib/object';

import { useTranslation } from '@yoweb/next-i18next';
import { Input } from '@yoweb/ui/components/form';
import { Grid } from '@yoweb/ui/components/Grid';
import { StatePicker } from '@yoweb/ui/components/StatePicker';
import AddressAutocomplete, { type Address } from '@yoweb/ui/components/AddressAutocomplete';
import { media } from '@yoweb/ui/styles/utils';
import {
  postalCodeOnChangeHandler,
  postalCodeOnPasteHandler,
  validatePostalCode,
} from '@yoweb/utils/postalCode';
import { getSpace } from '@yoweb/ui/styles/utils/theme';
import { useIntlMeta } from '@yoweb/ui/hooks/useIntlMeta';

// TODO: improve typing
export type PartialAddress = any;

export type AddressFormElementsProps = Pick<FormikProps<PartialAddress>, 'setValues' | 'values'> & {
  initialAddress?: PartialAddress;
  placesApiKey: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  fieldNamePrefix?: string;
};

export type AddressBaseSchema = OptionalObjectSchema<{
  street1: RequiredStringSchema<string | undefined>;
  street2: Yup.StringSchema<string>;
  city: RequiredStringSchema<string | undefined>;
  region: RequiredStringSchema<string | undefined>;
  postalCode: RequiredStringSchema<string | undefined>;
  country: RequiredStringSchema<string | undefined>;
}>;

export function useAddressSchema(): AddressBaseSchema {
  const { t } = useTranslation<'common'>();
  const { country } = useIntlMeta();

  return Yup.object({
    street1: Yup.string().trim().required(t('validation.required')),
    street2: Yup.string().trim().ensure(),
    city: Yup.string().trim().required(t('validation.required')),
    region: Yup.string().trim().required(t('validation.required')),
    postalCode: Yup.string()
      .trim()
      .test({
        message: t('validation.zip-code'),
        test: (value) => (value ? validatePostalCode(country, value) : false),
      })
      .required(t('validation.required')),
    country: Yup.string().trim().required(t('validation.required')),
  });
}

function AddressFormElements({
  initialAddress,
  setValues,
  values: currentAddress,
  placesApiKey,
  fieldNamePrefix = '',
  isRequired,
  isDisabled,
}: AddressFormElementsProps) {
  const { t } = useTranslation('contact-info');
  const { country, localeIsPostalCodeDominant } = useIntlMeta();
  const onAddressSelect = ({ city, postalCode, stateIsoCode, street, countryIsoCode }: Address) => {
    setValues({
      ...currentAddress,
      street1: street || '',
      city: city || '',
      region: stateIsoCode || '',
      postalCode: postalCode || currentAddress.postalCode,
      country: countryIsoCode || 'US',
    });
  };

  return (
    <Grid gap="normal1">
      {/* TODO: consider refactoring by locale to reduce complexity 
          if more conditions need to be introduced. */}
      {localeIsPostalCodeDominant ? (
        <>
          <Area>
            <AddressAutocomplete
              data-testid="address-element-postalCode"
              enableReinitialize
              name={`${fieldNamePrefix}postalCode`}
              inputMode="numeric"
              labelText={t('postal-code')}
              initialValue={initialAddress?.postalCode || currentAddress.postalCode}
              onAddressInputChanged={(postalCode) => {
                setValues({
                  ...currentAddress,
                  postalCode,
                });
              }}
              onAddressSelect={onAddressSelect}
              placesApiKey={placesApiKey}
              onChange={(e) => postalCodeOnChangeHandler(e, country)}
              onPaste={(e) => postalCodeOnPasteHandler(e, country)}
              isRequired={isRequired}
              disabled={isDisabled}
            />
            <StatePicker
              data-testid="address-element-region"
              name={`${fieldNamePrefix}region`}
              id="region"
              label={t('state')}
              isRequired={isRequired}
              disabled={isDisabled}
            />
          </Area>
          <Input
            data-testid="address-element-city"
            id="city"
            name={`${fieldNamePrefix}city`}
            label={t('city')}
            isRequired={isRequired}
            disabled={isDisabled}
          />
          <Input
            data-testid="address-element-street1"
            id="street1"
            name={`${fieldNamePrefix}street1`}
            label={t('address1')}
            isRequired={isRequired}
            disabled={isDisabled}
          />
          <Input
            data-testid="address-element-street2"
            id="street2"
            name="street2"
            label={t('address2')}
            disabled={isDisabled}
          />
        </>
      ) : (
        <>
          <AddressAutocomplete
            data-testid="address-element-street1"
            enableReinitialize
            name={`${fieldNamePrefix}street1`}
            labelText={t('address1')}
            initialValue={initialAddress?.street1 || currentAddress.street1}
            onAddressInputChanged={(street) => {
              setValues({
                ...currentAddress,
                street1: street,
              });
            }}
            onAddressSelect={onAddressSelect}
            placesApiKey={placesApiKey}
            isRequired={isRequired}
            disabled={isDisabled}
          />
          <Input
            data-testid="address-element-street2"
            id="street2"
            name={`${fieldNamePrefix}street2`}
            label={t('address2')}
            disabled={isDisabled}
          />
          <Input
            data-testid="address-element-city"
            id="city"
            name={`${fieldNamePrefix}city`}
            label={t('city')}
            isRequired={isRequired}
            disabled={isDisabled}
          />
          <Area>
            <StatePicker
              data-testid="address-element-region"
              name={`${fieldNamePrefix}region`}
              id="region"
              label={t('state')}
              isRequired={isRequired}
              disabled={isDisabled}
            />
            <Input
              data-testid="address-element-postalCode"
              id="postalCode"
              name={`${fieldNamePrefix}postalCode`}
              label={t('postal-code')}
              inputMode="numeric"
              onChange={(e) => postalCodeOnChangeHandler(e, country)}
              onPaste={(e) => postalCodeOnPasteHandler(e, country)}
              enforceCharacterWidth="half"
              isRequired={isRequired}
              disabled={isDisabled}
            />
          </Area>
        </>
      )}
    </Grid>
  );
}

const Area = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${getSpace('normal1')};

  ${media.md`
    grid-template-columns: 1fr 1fr;
  `}
`;

export default AddressFormElements;
